import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const EquipmentGrid = ({ equipment = [], onEquipmentAction, onMaintenanceSchedule }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const statusOptions = [
    { value: 'all', label: 'All Status', count: equipment?.length },
    { value: 'active', label: 'Active', count: equipment?.filter(e => e?.status === 'active')?.length },
    { value: 'idle', label: 'Idle', count: equipment?.filter(e => e?.status === 'idle')?.length },
    { value: 'maintenance', label: 'Maintenance', count: equipment?.filter(e => e?.status === 'maintenance')?.length },
    { value: 'offline', label: 'Offline', count: equipment?.filter(e => e?.status === 'offline')?.length }
  ];

  const sortOptions = [
    { value: 'name', label: 'Name' },
    { value: 'status', label: 'Status' },
    { value: 'utilization', label: 'Utilization' },
    { value: 'lastMaintenance', label: 'Last Maintenance' },
    { value: 'nextMaintenance', label: 'Next Maintenance' }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return 'Play';
      case 'idle': return 'Pause';
      case 'maintenance': return 'Wrench';
      case 'offline': return 'Power';
      default: return 'HelpCircle';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success';
      case 'idle': return 'text-warning';
      case 'maintenance': return 'text-error';
      case 'offline': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'active': return 'bg-success/10';
      case 'idle': return 'bg-warning/10';
      case 'maintenance': return 'bg-error/10';
      case 'offline': return 'bg-muted/10';
      default: return 'bg-muted/10';
    }
  };

  const getUtilizationColor = (utilization) => {
    if (utilization >= 80) return 'text-success';
    if (utilization >= 60) return 'text-warning';
    return 'text-error';
  };

  const getMaintenanceStatus = (nextMaintenance) => {
    const now = new Date();
    const maintenanceDate = new Date(nextMaintenance);
    const daysUntil = Math.ceil((maintenanceDate - now) / (1000 * 60 * 60 * 24));

    if (daysUntil < 0) return { status: 'overdue', color: 'text-error', text: 'Overdue' };
    if (daysUntil <= 7) return { status: 'due-soon', color: 'text-warning', text: `${daysUntil}d` };
    return { status: 'scheduled', color: 'text-success', text: `${daysUntil}d` };
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const filteredAndSortedEquipment = equipment?.filter(item => {
      const matchesSearch = item?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                           item?.type?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                           item?.location?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      const matchesStatus = statusFilter === 'all' || item?.status === statusFilter;
      return matchesSearch && matchesStatus;
    })?.sort((a, b) => {
      let aValue = a?.[sortBy];
      let bValue = b?.[sortBy];

      if (sortBy === 'utilization') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      } else if (sortBy === 'lastMaintenance' || sortBy === 'nextMaintenance') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Equipment Status</h3>
          <Button variant="outline" size="sm" iconName="Download">
            Export
          </Button>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0 lg:space-x-4">
          <div className="flex-1 max-w-md">
            <Input
              type="search"
              placeholder="Search equipment..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full"
            />
          </div>

          <div className="flex items-center space-x-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e?.target?.value)}
              className="px-3 py-1.5 text-sm border border-border rounded-md bg-background text-foreground"
            >
              {statusOptions?.map((option) => (
                <option key={option?.value} value={option?.value}>
                  {option?.label} ({option?.count})
                </option>
              ))}
            </select>

            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e?.target?.value?.split('-');
                setSortBy(field);
                setSortOrder(order);
              }}
              className="px-3 py-1.5 text-sm border border-border rounded-md bg-background text-foreground"
            >
              {sortOptions?.map((option) => (
                <React.Fragment key={option?.value}>
                  <option value={`${option?.value}-asc`}>{option?.label} ↑</option>
                  <option value={`${option?.value}-desc`}>{option?.label} ↓</option>
                </React.Fragment>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/30">
            <tr>
              <th className="text-left p-3 text-sm font-medium text-muted-foreground">Equipment</th>
              <th className="text-left p-3 text-sm font-medium text-muted-foreground">Status</th>
              <th className="text-left p-3 text-sm font-medium text-muted-foreground">Utilization</th>
              <th className="text-left p-3 text-sm font-medium text-muted-foreground">Last Maintenance</th>
              <th className="text-left p-3 text-sm font-medium text-muted-foreground">Next Maintenance</th>
              <th className="text-left p-3 text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedEquipment?.map((item, index) => {
              const maintenanceStatus = getMaintenanceStatus(item?.nextMaintenance);
              
              return (
                <tr 
                  key={item?.id} 
                  className={`border-b border-border hover:bg-muted/20 nav-transition ${
                    index % 2 === 0 ? 'bg-background' : 'bg-muted/10'
                  }`}
                >
                  <td className="p-3">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${getStatusBg(item?.status)}`}>
                        <Icon name={item?.icon || 'Settings'} size={20} className={getStatusColor(item?.status)} />
                      </div>
                      <div>
                        <div className="font-medium text-foreground">{item?.name}</div>
                        <div className="text-sm text-muted-foreground">{item?.type} • {item?.location}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center space-x-2">
                      <Icon name={getStatusIcon(item?.status)} size={16} className={getStatusColor(item?.status)} />
                      <span className={`text-sm font-medium capitalize ${getStatusColor(item?.status)}`}>
                        {item?.status}
                      </span>
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 bg-muted rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            item?.utilization >= 80 ? 'bg-success' : 
                            item?.utilization >= 60 ? 'bg-warning' : 'bg-error'
                          }`}
                          style={{ width: `${item?.utilization}%` }}
                        />
                      </div>
                      <span className={`text-sm font-medium ${getUtilizationColor(item?.utilization)}`}>
                        {item?.utilization}%
                      </span>
                    </div>
                  </td>
                  <td className="p-3">
                    <span className="text-sm text-muted-foreground">
                      {formatDate(item?.lastMaintenance)}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-muted-foreground">
                        {formatDate(item?.nextMaintenance)}
                      </span>
                      <span className={`text-xs font-medium ${maintenanceStatus?.color}`}>
                        ({maintenanceStatus?.text})
                      </span>
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center space-x-1">
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => onEquipmentAction && onEquipmentAction(item, 'view')}
                      >
                        <Icon name="Eye" size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => onMaintenanceSchedule && onMaintenanceSchedule(item)}
                      >
                        <Icon name="Calendar" size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => onEquipmentAction && onEquipmentAction(item, 'settings')}
                      >
                        <Icon name="Settings" size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filteredAndSortedEquipment?.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h4 className="text-lg font-medium text-foreground mb-2">No Equipment Found</h4>
            <p className="text-sm text-muted-foreground">
              Try adjusting your search terms or filters
            </p>
          </div>
        )}
      </div>
      {filteredAndSortedEquipment?.length > 0 && (
        <div className="p-4 border-t border-border">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              Showing {filteredAndSortedEquipment?.length} of {equipment?.length} equipment
            </span>
            <div className="flex items-center space-x-4">
              <span>Active: {equipment?.filter(e => e?.status === 'active')?.length}</span>
              <span>Maintenance: {equipment?.filter(e => e?.status === 'maintenance')?.length}</span>
              <span>Offline: {equipment?.filter(e => e?.status === 'offline')?.length}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EquipmentGrid;